import React, { useState, useEffect } from 'react';
import { useOrg } from '../../hooks/useOrg';
import { useOrgUsers } from '../../hooks/useOrgUsers';
import { useSurfaceTypes } from '../../hooks/useSurfaceTypes';
import { labRequestService } from '../../services/labRequestService';
import { departmentService, roomService } from '../../services/firebaseService';
import { useNotifications } from '../NotificationSystem';
import authService from '../../services/authService';
import './LabRequestForm.css';

const SAMPLE_TYPES = [
  { value: 'suprafete', label: 'Suprafețe' },
  { value: 'personal_medical', label: 'Personal medical' },
  { value: 'sterilitate', label: 'Sterilitate' },
  { value: 'aer', label: 'Aer' },
  { value: 'apa', label: 'Apă' }
];

const TEST_METHODS = [
  { value: 'cultura', label: 'Cultură' },
  { value: 'amf', label: 'AMF' },
  { value: 'citologie', label: 'Citologie' },
  { value: 'other', label: 'Altele' }
];

const INTERPRETATIONS = [
  { value: 'conform', title: 'Conform', subtitle: 'Rezultat în limite acceptate' },
  { value: 'neconform', title: 'Neconform', subtitle: 'Pozitiv / neconform' },
  { value: 'echivoc', title: 'Echivoc', subtitle: 'Necesită clarificare' },
  { value: 'na', title: 'N/A', subtitle: 'Nu se aplică' }
];

const LabRequestForm = ({ mode = 'create', labRequest = null, onSaved }) => {
  const { activeOrgId } = useOrg();
  const { notify } = useNotifications();
  const { users: orgUsers } = useOrgUsers();
  const { surfaceTypes } = useSurfaceTypes();

  const [departments, setDepartments] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedRequestId, setSavedRequestId] = useState(null);

  const currentUser = authService.getCurrentUser();

  const [requestForm, setRequestForm] = useState({
    department_id: '',
    department_name: '',
    room_id: '',
    room_name: '',
    surface_type_id: '',
    surface_type_name: '',
    sample_type: 'suprafete',
    test_method: 'cultura',
    test_method_description: '',
    operator_id: currentUser?.uid || '',
    operator_name: ''
  });

  const [resultForm, setResultForm] = useState({
    result_value: '',
    result_interpretation: '',
    result_notes: ''
  });

  const [requestErrors, setRequestErrors] = useState({});
  const [resultErrors, setResultErrors] = useState({});

  useEffect(() => {
    if (!activeOrgId) return;
    departmentService.getAll().then(setDepartments).catch(console.warn);
  }, [activeOrgId]);

  useEffect(() => {
    if (!requestForm.department_id) {
      setRooms([]);
      return;
    }
    roomService.getAll().then((all) => {
      setRooms(all.filter((r) => r.departmentId === requestForm.department_id));
    }).catch(console.warn);
  }, [requestForm.department_id]);

  useEffect(() => {
    if (requestForm.operator_id && orgUsers.length > 0) {
      const u = orgUsers.find((x) => x.userId === requestForm.operator_id);
      if (u) setRequestForm((prev) => ({ ...prev, operator_name: u.fullName }));
    }
  }, [requestForm.operator_id, orgUsers]);

  const handleRequestChange = (field, value) => {
    setRequestForm((prev) => ({ ...prev, [field]: value }));
    if (requestErrors[field]) setRequestErrors((prev) => { const e = { ...prev }; delete e[field]; return e; });
  };

  const handleResultChange = (field, value) => {
    setResultForm((prev) => ({ ...prev, [field]: value }));
    if (resultErrors[field]) setResultErrors((prev) => { const e = { ...prev }; delete e[field]; return e; });
  };

  const validateRequest = () => {
    const e = {};
    if (!requestForm.department_id) e.department_id = 'Departamentul este obligatoriu';
    if (!requestForm.room_id) e.room_id = 'Camera/salonul este obligatoriu';
    if (!requestForm.sample_type) e.sample_type = 'Tipul probei este obligatoriu';
    if (!requestForm.test_method) e.test_method = 'Metoda este obligatorie';
    setRequestErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateResult = () => {
    const e = {};
    if (!resultForm.result_value?.trim()) e.result_value = 'Rezultatul este obligatoriu';
    if (!resultForm.result_interpretation) e.result_interpretation = 'Interpretarea este obligatorie';
    setResultErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleCreateRequest = async () => {
    if (!validateRequest()) return;
    if (!activeOrgId) return;
    setSaving(true);
    try {
      const result = await labRequestService.createLabRequest(activeOrgId, requestForm, currentUser?.uid);
      setSavedRequestId(result.request_id);
      notify.success(`Cerere laborator nr. ${result.request_id} creată`);
      if (onSaved) onSaved(result);
    } catch (err) {
      notify.error('Eroare la creare cerere: ' + (err.message || String(err)));
    } finally {
      setSaving(false);
    }
  };

  const handleRecordResult = async () => {
    if (!validateResult() || !labRequest) return;
    if (!activeOrgId) return;
    setSaving(true);
    try {
      await labRequestService.recordLabResult(activeOrgId, labRequest.id, {
        ...resultForm,
        request_id: labRequest.request_id,
        room_id: labRequest.room_id,
        department_id: labRequest.department_id,
        sample_type: labRequest.sample_type,
        test_method: labRequest.test_method
      }, currentUser?.uid);
      notify.success(`Rezultat înregistrat pentru cererea #${labRequest.request_id}`);
      if (onSaved) onSaved();
    } catch (err) {
      notify.error('Eroare la înregistrare rezultat: ' + (err.message || String(err)));
    } finally {
      setSaving(false);
    }
  };

  const renderInterpretGrid = (value, onPick) => (
    <div className="lab-interpret-grid" role="group" aria-label="Interpretare rezultat">
      {INTERPRETATIONS.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={selected}
            aria-label={`${opt.title}: ${opt.subtitle}`}
            className={`lab-interpret-btn ${selected ? 'lab-interpret-btn--selected' : ''}`}
            onClick={() => onPick(opt.value)}
          >
            <span className="lab-interpret-btn__title">{opt.title}</span>
            <span className="lab-interpret-btn__sub">{opt.subtitle}</span>
          </button>
        );
      })}
    </div>
  );

  if (mode === 'result' && labRequest) {
    return (
      <div className="lab-request-shell">
        <div className="lab-request-card">
          <header className="lab-request-header">
            <h3 className="lab-request-title">Rezultat cerere laborator</h3>
            <p className="lab-request-id-line">
              Număr cerere (request_id): <strong className="lab-id-emphasis">#{labRequest.request_id}</strong>
            </p>
            <p className="lab-request-subtitle">{labRequest.room_name} — {labRequest.sample_type} — {labRequest.test_method}</p>
          </header>

          <div className="lab-request-form-inner">
            <div className="sampling-form-field">
              <span className="field-label">Rezultat / Valoare <span className="required">*</span></span>
              <textarea
                rows={4}
                value={resultForm.result_value}
                onChange={(e) => handleResultChange('result_value', e.target.value)}
                placeholder="ex: 15 germeni identificați, Cultură pozitivă Stafilococ aureus..."
              />
              {resultErrors.result_value && <span className="error">{resultErrors.result_value}</span>}
            </div>

            <div className="sampling-form-field sampling-form-field--interpret">
              <span className="field-label">Interpretare <span className="required">*</span></span>
              {renderInterpretGrid(resultForm.result_interpretation, (v) => handleResultChange('result_interpretation', v))}
              {resultErrors.result_interpretation && <span className="error">{resultErrors.result_interpretation}</span>}
            </div>

            <div className="sampling-form-field">
              <span className="field-label">Note</span>
              <textarea
                rows={2}
                value={resultForm.result_notes}
                onChange={(e) => handleResultChange('result_notes', e.target.value)}
                placeholder="Observații suplimentare..."
              />
            </div>

            <div className="lab-form-actions">
              <button type="button" className="btn btn-primary lab-submit-btn" onClick={handleRecordResult} disabled={saving}>
                {saving ? 'Se salvează...' : 'Înregistrează rezultat'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lab-request-shell">
      <div className="lab-request-card">
        <header className="lab-request-header">
          <h3 className="lab-request-title">Cerere nouă laborator</h3>
          <p className="lab-request-lead">
            La creare primești un <strong>număr cerere</strong> (<code>request_id</code>) — folosit la comunicarea cu laboratorul.
            {' '}
            Probele ATP au <strong>atp_id</strong> separat — vezi formularul „Test ATP”.
          </p>
        </header>

        {savedRequestId != null && (
          <div className="lab-saved-banner" role="status">
            <span>
              Cerere creată — număr: <strong className="lab-id-emphasis">#{savedRequestId}</strong>
            </span>
            <button type="button" className="lab-saved-dismiss" onClick={() => setSavedRequestId(null)} aria-label="Ascunde">×</button>
          </div>
        )}

        <div className="lab-request-form-inner">
          <div className="sampling-form-field">
            <span className="field-label">Departament <span className="required">*</span></span>
            <select
              value={requestForm.department_id}
              onChange={(e) => {
                const dept = departments.find((d) => d.id === e.target.value);
                handleRequestChange('department_id', e.target.value);
                handleRequestChange('department_name', dept?.name || '');
                handleRequestChange('room_id', '');
                handleRequestChange('room_name', '');
              }}
            >
              <option value="">Selectează departament...</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            {requestErrors.department_id && <span className="error">{requestErrors.department_id}</span>}
          </div>

          <div className="sampling-form-field">
            <span className="field-label">Salon / Cameră <span className="required">*</span></span>
            <select
              value={requestForm.room_id}
              onChange={(e) => {
                const room = rooms.find((r) => r.id === e.target.value);
                handleRequestChange('room_id', e.target.value);
                handleRequestChange('room_name', room?.name || '');
              }}
              disabled={!requestForm.department_id}
            >
              <option value="">Selectează salon...</option>
              {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            {requestErrors.room_id && <span className="error">{requestErrors.room_id}</span>}
          </div>

          <div className="sampling-form-field">
            <span className="field-label">Suprafață (opțional)</span>
            <select
              value={requestForm.surface_type_id}
              onChange={(e) => {
                const st = surfaceTypes.find((s) => s.id === e.target.value);
                handleRequestChange('surface_type_id', e.target.value);
                handleRequestChange('surface_type_name', st?.name || '');
              }}
            >
              <option value="">— Selectează suprafață (opțional) —</option>
              {surfaceTypes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          <div className="sampling-form-field">
            <span className="field-label">Tip probă <span className="required">*</span></span>
            <select value={requestForm.sample_type} onChange={(e) => handleRequestChange('sample_type', e.target.value)}>
              {SAMPLE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            {requestErrors.sample_type && <span className="error">{requestErrors.sample_type}</span>}
          </div>

          <div className="sampling-form-field">
            <span className="field-label">Metodă <span className="required">*</span></span>
            <select value={requestForm.test_method} onChange={(e) => handleRequestChange('test_method', e.target.value)}>
              {TEST_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            {requestErrors.test_method && <span className="error">{requestErrors.test_method}</span>}
          </div>

          {requestForm.test_method === 'other' && (
            <div className="sampling-form-field">
              <span className="field-label">Specificați metoda</span>
              <input
                type="text"
                value={requestForm.test_method_description}
                onChange={(e) => handleRequestChange('test_method_description', e.target.value)}
                placeholder="Descriere metodă..."
              />
            </div>
          )}

          <div className="sampling-form-field">
            <span className="field-label">Operator</span>
            <select value={requestForm.operator_id} onChange={(e) => handleRequestChange('operator_id', e.target.value)}>
              <option value="">Selectează operator...</option>
              {orgUsers.map((u) => <option key={u.userId} value={u.userId}>{u.fullName}</option>)}
            </select>
          </div>

          <div className="lab-form-actions">
            <button type="button" className="btn btn-primary lab-submit-btn" onClick={handleCreateRequest} disabled={saving}>
              {saving ? 'Se creează...' : 'Creează cerere'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LabRequestForm;
